import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { adminClient, formatApiError } from "@/admin/api";

const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "—";

const DetailRow = ({ label, value, mono = false, link = false }) => (
  <div className="grid grid-cols-3 gap-3 py-2.5 border-b border-[#F0EBE3] last:border-0">
    <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#78716C] pt-0.5">
      {label}
    </span>
    {value === null || value === undefined || value === "" ? (
      <span className="col-span-2 font-sans text-sm text-[#A8A29E]">—</span>
    ) : link ? (
      <a
        href={value}
        target="_blank"
        rel="noreferrer"
        data-testid="lead-detail-tf-link"
        className="col-span-2 font-mono text-xs text-[#C2410C] underline underline-offset-2 break-all"
      >
        {value}
      </a>
    ) : (
      <span
        className={`col-span-2 text-sm text-[#1A1715] break-all ${
          mono ? "font-mono text-xs" : "font-sans"
        }`}
      >
        {String(value)}
      </span>
    )}
  </div>
);

const LeadsViewer = ({ onUnauthorized }) => {
  const [data, setData] = useState({ leads: [], total: 0, page: 1, pages: 1 });
  const [page, setPage] = useState(1);
  const [campaign, setCampaign] = useState("");
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    adminClient()
      .get("/admin/campaigns")
      .then(({ data }) => setCampaigns(data.campaigns || []))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminClient().get("/admin/leads", {
        params: { page, limit: 20, campaign: campaign || undefined },
      });
      setData(data);
      setError("");
    } catch (err) {
      if (err.response?.status === 401) {
        onUnauthorized();
        return;
      }
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [page, campaign, onUnauthorized]);

  useEffect(() => {
    load();
  }, [load]);

  const campaignName = (id) =>
    campaigns.find((c) => c.id === id)?.name || id;

  return (
    <div data-testid="leads-viewer">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-serif font-semibold text-2xl text-[#1A1715]">
            Leads
          </h2>
          <p className="mt-1 font-sans text-sm text-[#78716C]">
            Read-only. Newest first. {data.total} total
            {campaign ? " for this filter" : ""}.
          </p>
        </div>
        <select
          data-testid="leads-campaign-filter"
          value={campaign}
          onChange={(e) => {
            setCampaign(e.target.value);
            setPage(1);
          }}
          className="bg-[#FFFFFF] border border-[#E7E2DA] rounded-sm px-3.5 py-2.5 font-sans text-sm text-[#1A1715] focus:outline-none focus:border-[#C2410C]"
        >
          <option value="">All campaigns</option>
          {campaigns.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <p
          data-testid="leads-error"
          role="alert"
          className="mt-6 border border-[#FECACA] bg-[#FEF2F2] rounded-sm px-4 py-3 font-sans text-sm font-medium text-[#B91C1C]"
        >
          {error}
        </p>
      )}

      <div className="mt-6 bg-[#FFFFFF] border border-[#E7E2DA] rounded-sm overflow-x-auto">
        <table className="w-full min-w-[720px] text-left">
          <thead>
            <tr className="border-b border-[#E7E2DA]">
              {["Submitted", "Reference", "Campaign", "Name", "Email", "Phone", "State"].map(
                (h) => (
                  <th
                    key={h}
                    className="px-5 py-3.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[#78716C] font-semibold"
                  >
                    {h}
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-5 py-10 text-center font-sans text-sm text-[#78716C]"
                >
                  Loading leads…
                </td>
              </tr>
            ) : data.leads.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  data-testid="leads-empty"
                  className="px-5 py-10 text-center font-sans text-sm text-[#78716C]"
                >
                  No leads found.
                </td>
              </tr>
            ) : (
              data.leads.map((l) => (
                <tr
                  key={l.lead_id}
                  data-testid={`lead-row-${l.lead_id}`}
                  onClick={() => setSelected(l)}
                  className="border-b border-[#F0EBE3] last:border-0 cursor-pointer hover:bg-[#FBF9F5] transition-colors"
                >
                  <td className="px-5 py-3.5 font-sans text-xs text-[#78716C] whitespace-nowrap">
                    {fmtDate(l.created_at)}
                  </td>
                  <td className="px-5 py-3.5 font-mono text-xs font-semibold text-[#1A1715]">
                    {l.lead_id}
                  </td>
                  <td className="px-5 py-3.5 font-sans text-sm text-[#44403C]">
                    {campaignName(l.campaign)}
                  </td>
                  <td className="px-5 py-3.5 font-sans text-sm text-[#1A1715]">
                    {l.first_name} {l.last_name}
                  </td>
                  <td className="px-5 py-3.5 font-sans text-xs text-[#44403C]">
                    {l.email}
                  </td>
                  <td className="px-5 py-3.5 font-mono text-xs text-[#44403C]">
                    {l.phone}
                  </td>
                  <td className="px-5 py-3.5 font-sans text-xs text-[#44403C]">
                    {l.state}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-5 flex items-center justify-between">
        <span
          data-testid="leads-pagination-info"
          className="font-mono text-xs text-[#78716C]"
        >
          Page {data.page} of {data.pages}
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            data-testid="leads-prev-page"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="inline-flex items-center gap-1 border border-[#E7E2DA] rounded-sm px-3.5 py-2 font-sans text-xs font-semibold text-[#44403C] hover:border-[#C2410C] hover:text-[#C2410C] disabled:opacity-40 disabled:pointer-events-none transition-colors"
          >
            <ChevronLeft className="h-3.5 w-3.5" /> Prev
          </button>
          <button
            type="button"
            data-testid="leads-next-page"
            disabled={page >= data.pages}
            onClick={() => setPage((p) => p + 1)}
            className="inline-flex items-center gap-1 border border-[#E7E2DA] rounded-sm px-3.5 py-2 font-sans text-xs font-semibold text-[#44403C] hover:border-[#C2410C] hover:text-[#C2410C] disabled:opacity-40 disabled:pointer-events-none transition-colors"
          >
            Next <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {selected && (
        <div
          data-testid="lead-detail-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-[#1A1715]/50 px-5"
          role="dialog"
          aria-modal="true"
          aria-label={`Lead ${selected.lead_id}`}
          onClick={(e) => {
            if (e.target === e.currentTarget) setSelected(null);
          }}
        >
          <div className="w-full max-w-2xl max-h-[85vh] overflow-y-auto bg-[#FFFFFF] border border-[#E7E2DA] rounded-sm p-7 shadow-[0_24px_60px_rgba(26,23,21,0.25)]">
            <div className="flex items-center justify-between">
              <div>
                <h3
                  data-testid="lead-detail-id"
                  className="font-mono font-semibold text-lg text-[#1A1715]"
                >
                  {selected.lead_id}
                </h3>
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#78716C] mt-1">
                  {fmtDate(selected.created_at)}
                </p>
              </div>
              <button
                type="button"
                data-testid="lead-detail-close"
                onClick={() => setSelected(null)}
                aria-label="Close"
                className="text-[#A8A29E] hover:text-[#1A1715] transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-6">
              <DetailRow label="Campaign" value={campaignName(selected.campaign)} />
              <DetailRow label="First name" value={selected.first_name} />
              <DetailRow label="Last name" value={selected.last_name} />
              <DetailRow label="Email" value={selected.email} />
              <DetailRow label="Phone" value={selected.phone} mono />
              <DetailRow label="State" value={selected.state} />
              <DetailRow label="ZIP" value={selected.zip} mono />
              <DetailRow label="Notes" value={selected.brief_notes} />
            </div>

            {selected.qualification_data &&
              Object.keys(selected.qualification_data).length > 0 && (
                <div className="mt-6" data-testid="lead-detail-qualification">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#78716C] mb-2">
                    Qualification Answers
                  </p>
                  {Object.entries(selected.qualification_data).map(([k, v]) => (
                    <DetailRow
                      key={k}
                      label={k}
                      value={Array.isArray(v) ? v.join(", ") : v}
                      mono
                    />
                  ))}
                </div>
              )}

            <div className="mt-6" data-testid="lead-detail-consent">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#78716C] mb-2">
                Consent Record
              </p>
              <DetailRow
                label="Consent given"
                value={
                  selected.consent_given ??
                  selected.consent_data?.consent_given
                    ? "Yes"
                    : "No"
                }
              />
              <DetailRow
                label="Version"
                value={
                  selected.consent_version ??
                  selected.consent_data?.disclosure_version
                }
                mono
              />
              <DetailRow
                label="Timestamp"
                value={
                  selected.consent_timestamp ??
                  selected.consent_data?.consent_timestamp
                }
                mono
              />
              {(selected.consent_text ||
                selected.consent_data?.disclosure_text) && (
                <p
                  data-testid="lead-detail-consent-text"
                  className="mt-3 border border-[#E7E2DA] bg-[#FBF9F5] rounded-sm p-4 font-sans text-xs leading-relaxed text-[#44403C]"
                >
                  {selected.consent_text ??
                    selected.consent_data?.disclosure_text}
                </p>
              )}
            </div>

            <div className="mt-6" data-testid="lead-detail-tech">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#78716C] mb-2">
                Technical Record
              </p>
              <DetailRow label="IP address" value={selected.ip_address} mono />
              <DetailRow
                label="TrustedForm"
                value={selected.trustedform_cert_url}
                link
              />
              <DetailRow
                label="Jornaya ID"
                value={selected.jornaya_lead_id}
                mono
              />
              <DetailRow label="Source" value={selected.source} mono />
              <DetailRow label="Landing page" value={selected.landing_page} mono />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadsViewer;
